import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';
import { PANEL_FONT_FAMILY } from '../../../utils/panel-font';
import {
  DASHBOARD_PLOT_HEIGHT,
  chartChrome,
  themePrimaryRamp,
} from '../../../utils/competition-chart';
import type { ProjectVisibilityRankDistribution } from '../../../types';
import { DashboardEmptyNote } from '../DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './ChartLegend';

const BUCKETS = [
  { key: 'rank1', label: 'رتبه ۱' },
  { key: 'rank2to3', label: 'رتبه ۲ تا ۳' },
  { key: 'rank4plus', label: 'رتبه ۴ به بعد' },
  { key: 'unranked', label: 'بدون رتبه' },
] as const;

function bucketColors(isDark: boolean) {
  return [...themePrimaryRamp(isDark), getColor('gray-400')];
}

const NO_COMPETITOR = '';

function brandKey(brand: ProjectVisibilityRankDistribution): string {
  return brand.knownBrandId ?? brand.canonicalName;
}

function RankDonut({
  brand,
  isDark,
  themeVariant,
  height,
  hiddenKeys,
}: {
  brand: ProjectVisibilityRankDistribution;
  isDark: boolean;
  themeVariant?: string;
  height: number;
  hiddenKeys: Set<string>;
}) {
  const option = useMemo(() => {
    const chrome = chartChrome();
    const colors = bucketColors(isDark);
    const total = brand.aiMentions;
    const firstShare = total > 0 ? Math.round((brand.rank1 / total) * 100) : null;

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (param: { name: string; value: number; percent: number }) =>
          `${param.name}: <strong>${param.value}</strong> پاسخ (${Math.round(param.percent)}%)`,
      },
      title: {
        text: firstShare == null ? '—' : `${firstShare}%`,
        subtext: firstShare == null ? '' : 'رتبه ۱',
        left: 'center',
        top: 'center',
        itemGap: 2,
        textStyle: { color: chrome.strongText, fontSize: 16, fontWeight: 'bold', fontFamily: PANEL_FONT_FAMILY },
        subtextStyle: { color: chrome.muted, fontSize: 10, fontFamily: PANEL_FONT_FAMILY },
      },
      series: [
        {
          type: 'pie',
          radius: ['62%', '88%'],
          avoidLabelOverlap: false,
          silent: total === 0,
          label: { show: false },
          itemStyle: { borderColor: chrome.surface, borderWidth: 2 },
          data:
            total === 0
              ? [{ name: 'بدون منشن', value: 1, itemStyle: { color: chrome.splitLine } }]
              : BUCKETS.map((bucket, index) => ({
                  name: bucket.label,
                  value: brand[bucket.key],
                  itemStyle: { color: colors[index] },
                })).filter((item) => item.value > 0 && !hiddenKeys.has(item.name)),
        },
      ],
    };
  }, [brand, isDark, hiddenKeys, themeVariant]);

  return (
    <ReactECharts
      key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
      option={option}
      notMerge
      style={{ height }}
      opts={{ renderer: 'svg' }}
    />
  );
}

/** Shows the focus brand's rank mix; one other brand can be picked to sit beside it. */
export function RankDistributionCard({
  brands,
  focusKey,
  focusName,
}: {
  brands: ProjectVisibilityRankDistribution[];
  /** When set, this brand takes the own-brand slot (domain filter). */
  focusKey?: string | null;
  focusName?: string | null;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };
  const colors = bucketColors(isDark);
  const [competitorKey, setCompetitorKey] = useState<string>(NO_COMPETITOR);
  const { hidden, toggle } = useLegendHidden();

  const focusBrand = focusKey
    ? (brands.find(
        (brand) =>
          brandKey(brand) === focusKey || (focusName != null && brand.canonicalName === focusName),
      ) ?? null)
    : (brands.find((brand) => brand.relationship === 'you') ?? null);
  const competitors = useMemo(
    () => brands.filter((brand) => (focusBrand ? brandKey(brand) !== brandKey(focusBrand) : brand.relationship !== 'you')),
    [brands, focusBrand],
  );
  // A competitor that left the data (e.g. after a filter change) is dropped.
  const competitor = competitors.find((brand) => brandKey(brand) === competitorKey) ?? null;
  const shown = [focusBrand, competitor].filter(
    (brand): brand is ProjectVisibilityRankDistribution => brand != null,
  );
  const donutHeight = DASHBOARD_PLOT_HEIGHT - 44;

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-3"
        title="توزیع رتبه در پاسخ‌های AI"
        endEl={
          competitors.length > 0 ? (
            <Form.Select
              size="sm"
              className="fs--2"
              style={{ width: '9rem' }}
              aria-label="انتخاب رقیب برای مقایسه"
              value={competitor ? competitorKey : NO_COMPETITOR}
              onChange={(event) => setCompetitorKey(event.target.value)}
            >
              <option value={NO_COMPETITOR}>مقایسه با رقیب…</option>
              {competitors.map((brand) => (
                <option key={brandKey(brand)} value={brandKey(brand)}>
                  {brand.canonicalName}
                </option>
              ))}
            </Form.Select>
          ) : null
        }
      />
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        <div className="flex-grow-1 d-flex align-items-center" style={{ minHeight: DASHBOARD_PLOT_HEIGHT }}>
          {shown.length === 0 ? (
            <DashboardEmptyNote>
              {brands.length === 0
                ? 'در این بازه برندی در پاسخ‌های AI دیده نشده.'
                : `${focusName ?? 'برند شما'} در پاسخ‌های این بازه دیده نشده.`}
            </DashboardEmptyNote>
          ) : (
            <Row className="g-2 justify-content-center w-100">
              {shown.map((brand) => (
                <Col xs={shown.length > 1 ? 6 : 8} key={`${brand.relationship}-${brandKey(brand)}`}>
                  <RankDonut
                    brand={brand}
                    isDark={isDark}
                    themeVariant={themeVariant}
                    height={donutHeight}
                    hiddenKeys={hidden}
                  />
                  <p
                    className={classNames(
                      'mb-0 text-center text-truncate fs--2',
                      focusBrand && brandKey(brand) === brandKey(focusBrand)
                        ? 'fw-bold text-1000'
                        : 'text-700',
                    )}
                    title={brand.canonicalName}
                  >
                    {brand.canonicalName}
                  </p>
                  <p className="mb-0 text-center fs--2 text-500">{brand.aiMentions} منشن</p>
                </Col>
              ))}
            </Row>
          )}
        </div>
        {brands.length > 0 ? (
          <ChartLegend
            onToggle={toggle}
            items={BUCKETS.map((bucket, index) => ({
              id: bucket.label,
              label: bucket.label,
              color: colors[index],
              hidden: hidden.has(bucket.label),
            }))}
          />
        ) : null}
      </Card.Body>
    </Card>
  );
}
